import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Copy, ExternalLink, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import AccountShell, { accountNav, isGateLocked } from "@/components/vangard/AccountShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREMIUM_TIERS = new Set(["gold", "vip", "corporate"]);

function Stat({ label, value, testid }) {
  return (
    <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-r border-white/[0.06] sm:border-b-0 last:border-r-0 sm:last:border-r-0" data-testid={testid}>
      <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{label}</div>
      <div className="font-serif text-xl sm:text-2xl mt-1.5 sm:mt-2 leading-none truncate">{value}</div>
    </div>
  );
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button
      type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={() => onChange(!on)} data-testid={testid}
      className={`inline-flex items-center py-2 ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
    >
      <span className={`relative flex h-6 w-11 items-center rounded-full transition-colors ${on ? "bg-white" : "bg-white/[0.12]"}`}>
        <span className={`inline-block h-4 w-4 rounded-full transition-transform ${on ? "bg-black translate-x-6" : "bg-white translate-x-1"}`} />
      </span>
    </button>
  );
}

export default function AccountOverviewPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [matches, setMatches] = useState([]);
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => { setIsPublic(!!user?.is_public); }, [user?.is_public]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/bookings`, { withCredentials: true })
      .then(({ data }) => setRequests(data || [])).catch(() => {});
    axios.get(`${API}/matching/matches`, { withCredentials: true })
      .then(({ data }) => setMatches(data.matches || [])).catch(() => {});
  }, [user]);

  if (!user) return <AccountShell active="/account"><div /></AccountShell>;

  const tier = (user.membership_tier || "silver").toLowerCase();
  const isPremium = PREMIUM_TIERS.has(tier);
  const status = user.application_status || "none";
  const approved = status === "approved";
  const firstName = (user.name || "").split(" ")[0];
  const profileUrl = user.slug ? `${window.location.origin}/u/${user.slug}` : null;
  const since = user.member_since
    ? new Date(user.member_since).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "—";

  const toggleVisibility = async (next) => {
    setIsPublic(next);
    try {
      await axios.patch(`${API}/auth/me`, { is_public: next }, { withCredentials: true });
      await refreshUser();
      toast.success(next ? t("vc.acct.now-public") : t("vc.acct.now-private"));
    } catch {
      setIsPublic(!next);
      toast.error(t("vc.account.save-fail"));
    }
  };

  const tiles = accountNav(t).filter((n) => n.to !== "/account" && n.to !== "/account/settings");

  return (
    <AccountShell active="/account" title={t("vc.acct.title")}>
      <div data-testid="account-overview-page">
        <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.acct.eyebrow")}</div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
          {t("vc.acct.greeting")} {firstName}.
        </h1>
        <p className="mt-4 text-base text-neutral-400 font-light max-w-xl">{t("vc.acct.sub")}</p>

        {/* Application status banner */}
        {!approved && (
          <div
            className="mt-10 rounded-3xl border border-white/[0.1] bg-white/[0.03] p-7 flex flex-col sm:flex-row sm:items-center gap-6 justify-between"
            data-testid="account-pending-banner"
          >
            <div className="max-w-xl">
              <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.acct.app-status")}</div>
              <div className={`font-serif text-2xl mt-2 ${status === "approved" ? "" : "text-[#E5484D]"}`} data-testid="overview-app-status">{t(`vc.onb.status.${status}`)}</div>
              <p className="text-sm text-neutral-400 font-light mt-2 leading-relaxed">{t("vc.acct.pending-hint")}</p>
            </div>
            <button
              onClick={() => navigate("/onboarding")}
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-white text-black px-7 py-3.5 rounded-full text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors"
              data-testid="account-finish-application"
            >
              {status === "none" || status === "draft" ? t("vc.acct.finish-app") : t("vc.acct.view-app")}
            </button>
          </div>
        )}

        {/* Stats strip */}
        <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 grid grid-cols-2 sm:grid-cols-4">
          <Stat label={t("vc.acct.tier")} value={tier.toUpperCase()} testid="stat-tier" />
          <Stat label={t("vc.acct.member-id")} value={`#${user.member_id || "—"}`} testid="stat-member-id" />
          <Stat label={t("vc.acct.since")} value={since} testid="stat-since" />
          <Stat
            label={isPremium ? t("vc.acct.matches") : t("vc.acct.requests")}
            value={isPremium ? matches.length : requests.length}
            testid="stat-count"
          />
        </div>

        {/* Public profile */}
        <div className="mt-6 rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-7 lg:p-9" data-testid="account-public-block">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="text-[9px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.account.visibility")}</div>
              <div className="font-serif text-2xl mt-2">
                {isPublic && isPremium ? t("vc.account.public") : t("vc.account.private")}
              </div>
              <p className="text-sm text-neutral-400 font-light mt-2 leading-relaxed max-w-xl">
                {isPremium
                  ? (isPublic ? t("vc.account.public-hint") : t("vc.account.private-hint"))
                  : t("vc.account.upgrade-hint")}
              </p>
            </div>
            <Toggle on={isPublic && isPremium} onChange={toggleVisibility} disabled={!isPremium} testid="overview-visibility-toggle" />
          </div>

          {profileUrl && (
            <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex-1 min-w-0 rounded-full border border-white/[0.1] px-5 py-3 text-sm font-light text-neutral-300 truncate" data-testid="account-profile-url">
                {profileUrl}
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(profileUrl); toast.success(t("vc.acct.copied")); }}
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="account-copy-link"
              >
                <Copy size={12} /> {t("vc.acct.copy")}
              </button>
              <a
                href={`/u/${user.slug}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-white/20 px-5 py-3 rounded-full text-[10px] tracking-[0.25em] uppercase hover:bg-white/5 hover:border-white/45 transition-colors"
                data-testid="account-open-public"
              >
                <ExternalLink size={12} /> {t("vc.acct.open")}
              </a>
            </div>
          )}
        </div>

        {/* Quick links */}
        <div className="mt-14 text-[10px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.acct.shortcuts")}</div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
          {tiles.map((n) => {
            const locked = isGateLocked(n, user);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`account-tile-${n.to.replace(/\//g, "-").slice(1)}`}
                className="group rounded-3xl border border-white/[0.08] bg-[#0A0A0A]/80 p-7 hover:border-white/25 hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start justify-between">
                  <n.icon size={18} strokeWidth={1.5} className="text-neutral-400 group-hover:text-white transition-colors" />
                  {locked
                    ? <Lock size={13} className="text-neutral-600" />
                    : <ArrowUpRight size={16} className="text-neutral-600 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />}
                </div>
                <div className="font-serif text-2xl mt-8">{n.label}</div>
                <p className="text-sm text-neutral-500 font-light mt-2">
                  {locked ? t("vc.acct.locked") : t(`vc.acct.tile.${n.index}`)}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </AccountShell>
  );
}
